/** Pantalla de login: formulario email/contraseña con POSTLogin, leyenda de error del backend (campo `mensaje`) y alternancia con vista de recuperar contraseña. */
import React, { useState } from "react";
import { Form, Input, Button, Typography, Divider } from "antd";
import { MdEmail, MdLock, MdArrowBack, MdErrorOutline, MdCheckCircle } from "react-icons/md";
import { LoggedUserInnerwork, LoginFormValues, LoginError } from "@/types";
import { POSTLogin } from "@/api";
import { useAuthStore } from "@/stores/authStore";
import "./login.css";

const { Title, Text } = Typography;

/** Mensaje legible del fallo de login: prioriza el `mensaje` del cuerpo del backend, acepta el formato legacy con "*" o un string plano. */
const mensajeDeError = (error: unknown): string => {
  if (error && typeof error === "object") {
    const mensaje = (error as Record<string, unknown>).mensaje;
    if (typeof mensaje === "string" && mensaje.trim()) return mensaje.trim();
  }
  if (typeof error === "string") {
    const fin = error.lastIndexOf("*");
    if (fin !== -1) return error.substring(fin + 1).trim();
    if (error.trim()) return error.trim();
  }
  return "No se pudo iniciar sesión. Verificá tus credenciales.";
};

const Login: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const setLoggedUserInnerwork = useAuthStore((s) => s.setLoggedUserInnerwork);
  const [loginError, setLoginError] = useState<LoginError | undefined>(undefined);
  const [view, setView] = useState<"login" | "recover">("login");

  const onFinish = async (values: LoginFormValues): Promise<void> => {
    try {
      setLoading(true);
      const { email, password } = values;
      await new Promise((resolve) => setTimeout(resolve, 500));

      const { success, data, error } = await POSTLogin({
        email,
        password,
      });
      if (success) {
        setLoggedUserInnerwork(data as LoggedUserInnerwork);
      } else if (error) {
        setLoginError({ message: mensajeDeError(error) });
      }
    } catch (error) {
      console.error(error);
      setLoginError({ message: mensajeDeError(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: Record<string, unknown>): void => {
    if (e) {
      setLoginError(undefined);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card-inner">
          <div className="login-promo">
            <div className="login-promo-content">
              <div className="login-promo-logo">
                <img src="/innercor-blanco.svg" alt="Innercor" width={250} />
              </div>
              <Title level={3} className="login-promo-title">
                Del diagnóstico a la facturación
              </Title>
              <span className="login-promo-accent" aria-hidden="true" />
              <Text className="login-promo-lead">
                Gestión de taller y finanzas en un solo lugar.
              </Text>
              <ul className="login-promo-list">
                <li>
                  <MdCheckCircle className="login-promo-list-icon" size={16} />
                  <span>Controlá cada orden y los tiempos de taller</span>
                </li>
                <li>
                  <MdCheckCircle className="login-promo-list-icon" size={16} />
                  <span>Conectá cada reparación directamente con tu caja</span>
                </li>
                <li>
                  <MdCheckCircle className="login-promo-list-icon" size={16} />
                  <span>Repuestos, horas técnicas y presupuestos sincronizados al instante</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="login-form-side">
            <div className="login-form-wrapper">
              <div className="login-form-main">
                {view === "login" ? (
                  <>
                    <Title level={2} className="login-title">
                      Iniciar Sesión
                    </Title>
                    <Text className="login-description">
                      Ingresa tus credenciales para acceder al sistema
                    </Text>

                    <Form
                      name="login"
                      className="login-form"
                      initialValues={{ remember: true }}
                      layout="vertical"
                      onFinish={onFinish}
                      autoComplete="off"
                      onValuesChange={(e) => handleInputChange(e)}
                    >
                      <Form.Item
                        name="email"
                        rules={[{ required: true, message: "Por favor ingrese su email" }]}
                      >
                        <Input
                          prefix={<MdEmail />}
                          placeholder="Email"
                          size="large"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </Form.Item>

                      <Form.Item
                        name="password"
                        rules={[{ required: true, message: "Por favor ingrese su contraseña" }]}
                      >
                        <Input.Password
                          prefix={<MdLock />}
                          placeholder="Contraseña"
                          size="large"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </Form.Item>

                      {loginError && (
                        <div className="error-wrapper" role="alert">
                          <MdErrorOutline size={14} />
                          <span>{loginError.message}</span>
                        </div>
                      )}

                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          block
                          size="large"
                        >
                          Iniciar sesión
                        </Button>
                      </Form.Item>
                    </Form>

                    <div className="forgetpw-wrapper">
                      <Button type="link" onClick={() => setView("recover")}>
                        ¿Olvidaste tu contraseña?
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Button
                      type="text"
                      icon={<MdArrowBack />}
                      onClick={() => setView("login")}
                      className="recover-back"
                    >
                      Volver al login
                    </Button>

                    <Title level={2} className="login-title">
                      Recuperar contraseña
                    </Title>
                    <Text className="login-description">
                      Ingresá tu email y te enviaremos las instrucciones para restablecer tu
                      contraseña
                    </Text>

                    <Form
                      name="recover"
                      className="login-form"
                      layout="vertical"
                      onFinish={() => {}}
                      autoComplete="off"
                    >
                      <Form.Item
                        name="email"
                        rules={[
                          { required: true, message: "Por favor ingrese su email" },
                          { type: "email", message: "Ingrese un email válido" },
                        ]}
                      >
                        <Input
                          prefix={<MdEmail />}
                          placeholder="Email"
                          size="large"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button type="primary" htmlType="submit" block size="large">
                          Enviar instrucciones
                        </Button>
                      </Form.Item>
                    </Form>
                  </>
                )}
              </div>
              <div className="login-version">
                <Divider />
                <div className="login-version-row">
                  <span className="login-version-copyright">
                    ©{" "}
                    <a href="https://innercor.com.ar" target="_blank" rel="noopener noreferrer">
                      {new Date().getFullYear()} Innercor Soluciones ERP
                    </a>
                  </span>
                  <span className="login-version-number">v{import.meta.env.VITE_VERSION}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
